import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { invoiceDiscarded, invoiceUpdated, invoiceCreated } from "store/actions/invoiceActions";
//#endregion
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { InvoiceWizardMoreActionProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { DialogEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import { MoreActions, useCheckRelations } from "components/common";
import useAssignNewOwner from "../../actions/useAssignOwner";
import ForwardedCostsReminder from "./costForwarding/forwardedCostsReminder";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { ForwardedCostHelper, LocalizationHelperInstance } from "utils/helpers";

const InvoiceWizardMoreActions: React.FC<InvoiceWizardMoreActionProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { isFormValid, createInvoiceHandler, reloadInvoiceHandler, invoiceId, invoiceOwnerId, forwardedCosts } = props;

  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const isDialogOpen = useSelector((store: AppState) => store.util.isDialogOpen);

  const { getIsReadonlyUser } = useCheckRelations();

  const { getAssignOwnerAction } = useAssignNewOwner(
    invoiceId,
    invoiceOwnerId,
    invoiceId ? reloadInvoiceHandler.bind(null, invoiceId) : undefined
  );

  const invoiceSaveDraftSuccess = (invoiceId: number) => {
    invoiceService.get(invoiceId).then((i) => {
      if (props.invoiceId) {
        dispatch(invoiceUpdated(i));
      } else {
        dispatch(invoiceCreated(i));
      }
    });

    reloadInvoiceHandler(invoiceId);
  };

  const saveAsDraftAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDraft),
    icon: IconDefinitionConstants.WEdit,
    onClickHandler: createInvoiceHandler.bind(null, undefined, true),
    successMessage: props.isQuotation
      ? TranslationKeys.Quotation.SaveSuccessMessage
      : TranslationKeys.Invoice.SaveSuccessMessage,
    inProgressMessage: TranslationKeys.Common.Saving,
    onActionSuccess: invoiceSaveDraftSuccess,
  };

  const discardInvoice = async () => {
    if (invoiceId) {
      await invoiceService.deleteInvoice(invoiceId);
    }
  };

  const onDiscardSuccess = () => {
    if (invoiceId) {
      dispatch(invoiceDiscarded(invoiceId));
      reloadInvoiceHandler(invoiceId);
    }
  };

  const discardAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Discard),
    icon: IconDefinitionConstants.WTrashAlt,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Discard),
      titleIcon: IconDefinitionConstants.WTrashAlt,
      dialogType: DialogEnums.DialogType.Message,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
          onClick: discardInvoice,
          type: StyleEnums.DisplayType.Danger,
          successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CancelSuccessMessage),
        },
      ],
      children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CancelWarning),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: onDiscardSuccess,
  };

  const addForwardedCostsAction: MoreActionMenuItemType | undefined = forwardedCosts?.canForwardCosts
    ? {
        description: ForwardedCostHelper.getForwardedCostsMenuText(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsAdd),
          forwardedCosts.summary
        ),
        icon: IconDefinitionConstants.WReceipt,
        onClickHandler: async () => forwardedCosts.openDialog(),
      }
    : undefined;

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const title: MoreActionMenuItemType[] = [TranslationKeys.Common.Actions];
    const actions: MoreActionMenuItemType[] = [];

    if (getIsReadonlyUser()) return [];

    if (isFormValid) {
      actions.push(saveAsDraftAction);
    }

    if (addForwardedCostsAction) actions.push(addForwardedCostsAction);

    if (invoiceId) actions.push(discardAction);

    const assignNewOwnerAction = getAssignOwnerAction();
    if (assignNewOwnerAction) {
      actions.push(assignNewOwnerAction);
    }

    return actions.length > 0 ? title.concat(actions) : [];
  };

  const moreActions = <MoreActions menuItems={getMenuItems()} menuStateChangeCallback={setIsMenuOpen} />;

  // The reminder would sit on top of the opened menu, which already lists the same entry, and it shares
  // the dialogs' layer, so it steps aside for them the way the help button does.
  return forwardedCosts ? (
    <ForwardedCostsReminder
      show={forwardedCosts.showReminder && !isMenuOpen && !isDialogOpen}
      customerName={forwardedCosts.customerName}
      summary={forwardedCosts.summary}
      onOpen={forwardedCosts.openDialog}
      onClose={forwardedCosts.dismissReminder}
    >
      {moreActions}
    </ForwardedCostsReminder>
  ) : (
    moreActions
  );
};

export default InvoiceWizardMoreActions;
